import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { CustomMatPaginatorIntl } from '../custom-mat-paginator-intl';
import { safeJsonParse, safeJsonStringify, safeJsonClone } from '@ebw-monorepo/microservices-helpers/utils/parser';

@Component({
  selector: 'ebw-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  providers: [{ provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl }]
})
export class ListComponent implements OnInit {
  constructor() {}

  /**
   * Variable to control the loading status of the list.
   */
  @Input() public loading: boolean = false;

  AUTOMATIC_PAGINATION_ELEMENTS = 10;

  /**
   * Data input from the title of the list
   */
  @Input() title: string = '';

  /**
   * Data input from the parent component to display.
   *
   * This variable contains all the values that the parent sends to the component.
   */
  @Input() data: {}[] = [];

  /**This is the list AFTER filters has been applied */
  filteredData: {}[] = [];

  /**
   * This is the partial part of the list being displayed
   */
  listToDisplay: {}[] = [];

  /**
   * List of fields. Must contain at least 1 field.
   * Structure => {name: string, type: string, property: string }[]
   *
   * name is the name of the field (human readable). Mandatory.
   *
   * type is the type of field: string | datetime | number. Mandatory.
   *
   * property is the actual field in the data object. Mandatory.
   */
  @Input() fields: {
    name: string;
    type: string;
    property: string;
    style?: any;
    headerStyle?: any;
    contentStyle?: any;
    conditionContentStyle?: object;
  }[] = [];

  /** This are the fields that are actually being displayed, to save the user input fields.
   * This allows to modify the fields object without modifying the input.
   */
  displayFields: {
    name: string;
    type: string;
    property: string;
    style?: any;
    headerStyle?: any;
    contentStyle?: any;
    conditionContentStyle?: object;
  }[] = [];

  displayedColumns: string[] = [];

  /**
   * Filter object that controls the filter of the list.
   * The property filterFields has the list of properties of the list in which the filter will be applied.
   * The filterPlaceholder has the text that is put as a placeholder of the input field of the filter.
   */
  @Input() filter: {
    filterFields: string[]; //The properties of the filter
    filterPlaceholder: string;
  } = { filterFields: [], filterPlaceholder: 'Filter' };

  /**
   * Variable that holds the filter input field.
   */
  listFilter: string = '';

  /**
   * List of fields that are sortable.
   */
  @Input() sortableFields: String[] = [];

  /**
   * Flag to enable/disable the clickable rows.
   *
   * Disabled by default if no value is specified.
   */
  @Input() clickableRows: boolean = false;

  /**
   * Variable that holds the current field in which the list is sorted.
   */
  currentField: String = '';

  /**
   * Variable that holds the current direction of sorting from a field.
   *
   * Direction can be 1 (descending), -1 (ascending), or 0 (no order)
   */
  currentDirection: number = 0;

  /**
   * Number of pages that the data is divided in.
   *
   * Calculated on init.
   */
  pages: number = 0;

  /**
   * Pagination object.
   * Controls the pagination and options.
   * - enable: property to enable and disabling pagination. By default it is null, meaning that it is the default behavior. If user input exists
   *   then the enable is true or false.
   * - topPagination: optional field that displays the pages control on top of the list. True by default.
   * - topPagination: optional field that displays the pages control at the bottom of the list. True by default
   * - elementsPerPage: Number of elements per page. By default corresponds the AUTOMATIC_PAGINATION_ELEMENTS.
   */
  @Input() pagination: {
    enable: boolean | null;
    topPagination?: boolean;
    bottomPagination?: boolean;
    elementsPerPage: number;
  } = {
    enable: null,
    topPagination: true,
    bottomPagination: true,
    elementsPerPage: this.AUTOMATIC_PAGINATION_ELEMENTS
  };

  @ViewChild(MatPaginator) paginator: MatPaginator;

  /**
   * Variable that holds the current page number.
   *
   * Pages go from 1 to "pages".
   */
  currentPage: number = 0;

  /**
   * Index of the first element in the displayed list with respect with the data list.
   *
   * Used for pagination.
   */
  lowerIndex: number = 0;

  /**
   * Index of the last element in the displayed list with respect with the data list.
   *
   * Used for pagination.
   */
  upperIndex: number = 0;

  /**
   * Variable that enables or disables the selecting properties of the list.
   * If it is true, it adds a checkbox element at the beggining of the list to select/deselect multiple elements of the list and
   * perform and action in bulk with them.
   */
  @Input() selectable: boolean = false;

  /**
   * Checkbox to select/deselect all the elements in the page.
   * It works also as the checkbox field header.
   */
  selectAllCheckbox: boolean = false;

  /**
   * Event emitter for a row click.
   *
   * Triggered when a row is clicked and the property clickableRows is true.
   */
  @Output() rowClicked = new EventEmitter<any>();

  /**
   * Event emitter for a change in a checkbox.
   *
   * Triggered when a single checkbox is changed in a checkbox field or when the checkbox to select all the rows is changed.
   */
  @Output() rowSelected = new EventEmitter<any>();

  /**
   * Event emitter that gets triggered when the button action of the select is clicked.
   *
   * This sends the data of the the selected elements as a list to the parent component.
   */
  @Output() selectedItemsAction = new EventEmitter<any>();

  /**
   * Text of the action button for the selected elements. It is managed by the parent as an Input.
   */
  @Input() selectedItemsButtonText: string = '';

  /**
   * For reference check: https://stackoverflow.com/questions/35190188/can-an-ng-content-be-used-inside-of-an-ngfor/60725235#60725235
   *
   * This variable allows to insert multiple html content into the custom field.
   *
   * To use the custom field put the custom html code inside the <ebw-list> tag and inside this
   * <ng-template let-element #customField></ng-template>. The variable 'element' exposes the element of the list
   * and the #customField property tells the list where is the code intended to go.
   * The 'element' variable can have a different name. For example, let-i works exactly the same, just that in the parent component
   * the exposed variable name will be 'i' instead of 'element'.
   *
   * The list accepts one custom field and is added to the last column of the list
   */
  @ContentChild('customField', { static: false }) customFieldTemplateRef: TemplateRef<any>;

  /**
   * Event emitter that gets triggered when the button action of the select is clicked.
   *
   * This sends the data of the the selected elements as a list to the parent component.
   */
  @Output() onChangesData = new EventEmitter<any>();

  @Input() pageNumber: number;

  async ngOnInit(): Promise<void> {
    this.initList();
  }

  /**
   * Initializes the component by:
   *
   * - Automatically builds a 'fields' object if there is none provided by the parent. This detects the data type of the fields of an element of the list and constructs the 'fields' object using it.
   * - Calculate the variables related to pagination and build the filteredList and the listToDisplay by calling goToPage(1).
   * - Sets up the custom field if it exists.
   * - Checks if all items are selected to modify the checkbox that selects/unselects all rows.
   */
  initList() {
    if (this.data && this.data.length > 0) {
      //Set up fields
      this.displayFields = [];
      if (this.fields.length == 0) {
        //automatically detect fields
        let dataRow = this.data[0];
        for (let [key, value] of Object.entries(dataRow)) {
          this.displayFields.push({
            name: key,
            type: typeof value,
            property: key
          });
        }
      } else {
        //If the fields input is provided, use it.
        //Create a deep copy of fields
        this.displayFields = safeJsonClone(this.fields);
      }

      //Add the custom field at the end if there is a content projection
      if (this.customFieldTemplateRef) {
        this.displayFields.push({
          name: 'custom',
          type: 'custom',
          property: ''
        });
      }

      if (this.selectable) {
        //add the checkbod column
        this.displayFields.unshift({
          name: 'select',
          type: 'checkbox',
          property: ''
        });

        //add the selected field to the list
        for (let element of this.listToDisplay) {
          element['selected'] = false;
        }
      }

      //Build the column object after adding the checkbox and custom field
      this.displayedColumns = this.displayFields.map((element) => {
        return element.name;
      });

      //aggiungo la proprietà 'content-style' ad ogni item della lista
      //cerco il field corrispondente e setto il contentStyle se non di tipo condizionato altrimenti lo calcolo
      this._setItemContentStyle(this.data, this.displayFields);

      this.filteredData = this.data;

      this.setUpPagination();

      this.checkIfAllItemsSelected();
    } else {
      //When no data is present
      this.filteredData = [];
      this.listToDisplay = [];
      this.checkIfAllItemsSelected();
      this.setUpPagination();
    }
  }

  setUpPagination() {
    //Set up pagination
    if (this.pagination.enable == null) {
      this.pagination.enable = this.filteredData.length > this.AUTOMATIC_PAGINATION_ELEMENTS ? true : false;
    }
    if (this.pagination.enable) {
      this.pages = Math.ceil(this.filteredData.length / this.pagination.elementsPerPage);
      this.currentPage = 0;
      this.lowerIndex = 0;
      this.upperIndex = this.pagination.elementsPerPage;

      this.goToPage(0);
    } else {
      this.listToDisplay = this.filteredData;
    }
  }

  /**
   * Check if all the checkboxes in the list are selected.
   */
  checkIfAllItemsSelected() {
    let allChecked = true && this.listToDisplay.length > 0;
    this.listToDisplay.forEach((element: any) => {
      allChecked = allChecked && element.selected;
    });
    this.selectAllCheckbox = allChecked;
  }

  /**
   * Method to return an array of a range specified.
   *
   * This builds an array of the form [init, init+1, ... , end-1, end].
   * @param init starting number of the range.
   * @param end final number of the range.
   * @returns Array of the form [init, init+1, ... , end-1, end].
   */
  range(init: number, end: number) {
    let arr = [];
    for (let i = init; i <= end; i++) {
      arr.push(i);
    }
    return arr;
  }

  /**
   * Changes the page displayed on the list. This controls also all the conditions and constraints that the
   * list have.
   * @param page The page number. Pages start in 1.
   */
  goToPage(page: number) {
    if (page <= this.pages && page >= 0) {
      this.currentPage = page;
      this.lowerIndex = this.pagination.elementsPerPage * this.currentPage;
      const upperLimit = this.lowerIndex + this.pagination.elementsPerPage;
      this.upperIndex = upperLimit < this.filteredData.length ? upperLimit : this.filteredData.length;
      this.listToDisplay = this.filteredData.slice(this.lowerIndex, this.upperIndex);
    } else {
      this.currentPage = 0;
      this.lowerIndex = 0;
      this.upperIndex = 0;
      this.listToDisplay = [];
    }
    this.checkIfAllItemsSelected();
  }

  handlePageChange($event) {
    //Internal pagination element starts from 1
    //Material pagination starts from 0
    this.goToPage($event.pageIndex);
  }

  orderColumn(property, direction) {
    this.currentDirection = direction;
    this.currentField = property;
    this.applyFilters();
  }

  /**
   * Centralized function per filtering and ordering the list
   */
  applyFilters() {
    //Make a deep copy of the original list
    this.filteredData = safeJsonParse<{}[]>(safeJsonStringify(this.data) ?? '', this.data);

    //Filters by name
    let filterName = this.listFilter.toUpperCase();
    this.filteredData = this.filteredData.filter((element) => {
      let matches = false;
      for (let i in this.filter.filterFields) {
        let filterElement = this.filter.filterFields[i];
        const propertyToFilter = this.resolveObjectProperty(element, filterElement);
        if (propertyToFilter) {
          const propertyToFilterString = propertyToFilter.toString();
          matches = matches || propertyToFilterString.toUpperCase().includes(filterName);
        }
      }
      return matches;
    });

    if (this.currentDirection != 0) {
      if (this.currentDirection == 1) {
        //Ascending order
        this.filteredData.sort((element1, element2) => {
          let element1_value = this.resolveObjectProperty(element1, this.currentField);
          if (element1_value == null) element1_value = '';
          let element2_value = this.resolveObjectProperty(element2, this.currentField);
          if (element2_value == null) element2_value = '';

          if (element1_value == element2_value) return 0;
          if (element1_value > element2_value) return 1;
          if (element1_value < element2_value) return -1;
          return 0;
        });
      } else {
        //Descending order
        this.filteredData.sort((element1, element2) => {
          let element1_value = this.resolveObjectProperty(element1, this.currentField);
          if (element1_value == null) element1_value = '';
          let element2_value = this.resolveObjectProperty(element2, this.currentField);
          if (element2_value == null) element2_value = '';

          if (element1_value == element2_value) return 0;
          if (element1_value < element2_value) return 1;
          if (element1_value > element2_value) return -1;
          return 0;
        });
      }
    }
    this.setUpPagination();
  }

  /**
   * Reload the list on changes done in the parent component.
   * @param changes SimpleChanges object with the changes.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.initList();
    this.onChangesData.emit(true);
  }

  /**
   * Triggers the rowClicked event when a row is clicked and the property clickableRows is true.
   *
   * This send the entire row element as the $event data parameter.
   * @param rowData Complete row data element.
   */
  rowClickedEmit(rowData: any): void {
    if (this.clickableRows) {
      this.rowClicked.emit(rowData);
    }
  }

  emitSelectedItemsAction() {
    try {
      let selectedRows = this.filteredData.filter((element: any) => {
        return element.selected;
      });
      this.selectedItemsAction.emit(selectedRows);
    } catch (error) {
      //Use trycatch to avoid the global loading to block the application
      console.log(error);
    }
  }

  /**
   * Triggers the checkboxChangedEmitter event when a checkbox state is changed.
   *
   * This send the entire row element as the $event data parameter.
   * @param rowData
   */
  checkboxChanged(rowData: any): void {
    rowData.selected = !rowData.selected;
    this.checkIfAllItemsSelected();
    this.rowSelected.emit(rowData);
  }

  /**
   * Method to select or deselect all checkboxes in the list page. This also emits checkboxChangedEmitter.
   */
  checkAllItems() {
    let valueToSet = true; //Check them all by default
    if (this.selectAllCheckbox) {
      //If all values checked, unselect them
      valueToSet = false;
    }

    this.listToDisplay.forEach((element: any) => {
      element.selected = valueToSet;
    });

    this.selectAllCheckbox = valueToSet;
    this.rowSelected.emit();
  }

  /**
   * Returns the value for the specified property in item, even when the property is into another property.
   * For example if the item is {a: {b: "value"}}, and the property is "a.b", it returns the value "value".
   * @param item Item to extract the value from
   * @param property Property name. Can be nested. For example "a.b" can be specified to access the propery "a" and then its propery "b".
   * @returns The value of "property" in the "item".
   */
  resolveObjectProperty(item: any, property: String) {
    try {
      let propertySequence = property.split('.');
      let objectProperty = item;
      for (let prop in propertySequence) {
        objectProperty = objectProperty[propertySequence[prop]];
      }

      return objectProperty;
    } catch (error) {
      return '';
    }
  }

  private _setItemContentStyle(data: any[], fields: any[]) {
    if (!data) {
      return;
    }

    data.forEach((item) => {
      item['contentStyle'] = {};
      let style = {};

      fields.forEach((field) => {
        if (field.contentStyle) {
          style = safeJsonClone(field.contentStyle);
        }

        if (field.conditionContentStyle) {
          style = Object.assign({}, style, this._buildCssObjFromConditionStyle(field.conditionContentStyle, item));
        }
        item['contentStyle'][field.property] = style;
      });
    });
  }

  private _buildCssObjFromConditionStyle(styleObj, item) {
    let cssObj = {};

    styleObj.forEach((_) => {
      let val: string;

      if (_.keys && _.keys.length > 0) {
        val = _.value;
        _.keys.forEach((key, i) => {
          val = val.replaceAll(`{${i}}`, String(item[key] ?? ''));
        });
      } else {
        val = `${_.value}`;
      }

      cssObj[_.cssProperty] = val;
    });

    return cssObj;
  }
}
